import { useEffect, useState } from "react";
import { Copy, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { INSIGHTS_SENT, INSIGHTS_INT, INSIGHTS_OBJ_LABEL } from "./insights";

function CompareColumn({ label, data, cid, onChange, campaigns }) {
  return (
    <div className="flex-1 min-w-0" data-testid={`ai-outbound-compare-col-${label.toLowerCase()}`}>
      <div className="mb-2 text-[10px] uppercase tracking-wide text-slate-500">Campaign {label}</div>
      <Select value={cid} onValueChange={onChange}>
        <SelectTrigger data-testid={`ai-outbound-compare-select-${label.toLowerCase()}`}>
          <SelectValue placeholder="Pick a campaign" />
        </SelectTrigger>
        <SelectContent>
          {campaigns.map((c) => (
            <SelectItem key={c.campaign_id} value={c.campaign_id}>
              {c.campaign_name} ({c.total_scored})
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {!data ? (
        <div className="mt-3 rounded-md border border-dashed border-slate-200 p-4 text-center text-xs text-slate-400">
          Pick a campaign
        </div>
      ) : (
        <div className="mt-3 space-y-2">
          <div className="text-xs text-slate-500">
            {data.total_scored} scored · {data.total_calls} total
          </div>
          {["positive", "neutral", "negative"].map((k) => {
            const c = data.sentiment_counts[k]; const pct = data.total_scored ? Math.round((c / data.total_scored) * 100) : 0;
            return (
              <div key={k} className="flex items-center gap-2 text-xs">
                <div className={`w-16 shrink-0 font-medium ${INSIGHTS_SENT[k].text}`}>{INSIGHTS_SENT[k].label}</div>
                <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className={`h-full ${INSIGHTS_SENT[k].bg}`} style={{ width: `${c ? Math.max(4, pct) : 0}%` }} />
                </div>
                <div className="w-14 shrink-0 text-right tabular-nums text-slate-500">{c} · {pct}%</div>
              </div>
            );
          })}
          <div className="pt-1" />
          {["hot", "warm", "cold"].map((k) => {
            const c = data.interest_counts[k]; const pct = data.total_scored ? Math.round((c / data.total_scored) * 100) : 0;
            return (
              <div key={k} className="flex items-center gap-2 text-xs">
                <div className={`w-16 shrink-0 font-medium ${INSIGHTS_INT[k].text}`}>{INSIGHTS_INT[k].label}</div>
                <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className={`h-full ${INSIGHTS_INT[k].bg}`} style={{ width: `${c ? Math.max(4, pct) : 0}%` }} />
                </div>
                <div className="w-14 shrink-0 text-right tabular-nums text-slate-500">{c} · {pct}%</div>
              </div>
            );
          })}
          {data.objections_top?.length > 0 && (
            <div className="pt-2">
              <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Top objections</div>
              <div className="flex flex-wrap gap-1">
                {data.objections_top.map((o) => (
                  <span key={o.key} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-700">
                    {INSIGHTS_OBJ_LABEL[o.key] || o.key}
                    <span className="rounded-full bg-slate-200 px-1 text-[9px] text-slate-600">{o.count}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function CampaignCompareDialog({ open, onClose, campaigns, since }) {
  const [aId, setAId] = useState("");
  const [bId, setBId] = useState("");
  const [aData, setAData] = useState(null);
  const [bData, setBData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (!aId && campaigns.length > 0) setAId(campaigns[0].campaign_id);
    if (!bId && campaigns.length > 1) setBId(campaigns[1].campaign_id);
  }, [open, campaigns, aId, bId]);

  useEffect(() => {
    if (!open || !aId || !bId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [ra, rb] = await Promise.all([
          api.get(`/outbound/campaigns/${aId}/insights?since=${since}`),
          api.get(`/outbound/campaigns/${bId}/insights?since=${since}`),
        ]);
        if (!cancelled) { setAData(ra.data); setBData(rb.data); }
      } catch {
        if (!cancelled) { setAData(null); setBData(null); }
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open, aId, bId, since]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-3xl" data-testid="ai-outbound-compare-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="h-4 w-4 text-primary" /> Compare campaigns
          </DialogTitle>
          <DialogDescription>
            Side-by-side sentiment + interest mix and top objections for two campaigns. Window: {since === "all" ? "All time" : `Last ${since}`}.
          </DialogDescription>
        </DialogHeader>
        {loading && <div className="text-center text-xs text-slate-400 py-2"><Loader2 className="inline h-3 w-3 animate-spin" /> Loading…</div>}
        <div className="flex flex-col gap-4 sm:flex-row">
          <CompareColumn label="A" data={aData} cid={aId} onChange={setAId} campaigns={campaigns} />
          <CompareColumn label="B" data={bData} cid={bId} onChange={setBId} campaigns={campaigns} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="ai-outbound-compare-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
